/*
 * @cca/ui — LoginScreen flow logic (framework-free)
 * ---------------------------------------------------------------------------
 * The estate's ONE sign-in ceremony, extracted from React so it is unit-testable
 * with a mocked fetch and reusable across transports (the ADR-018 "transport-
 * neutral handler" idea, applied to the CLIENT). `LoginScreen.tsx` is the thin
 * React shell that owns the useState cells and hands their setters to these pure
 * functions; every branch that matters — the frozen status→outcome contract, the
 * OTP fallback decision, the message mapping, the submit orchestration — lives
 * here, so the behaviour is proven without a DOM (the repo has no jsdom).
 *
 * The client outcome switch is a FROZEN contract (Gate-2, ADR-018 increment 2):
 *   503 passkeys_unavailable → fall through to email (send an OTP)
 *   429 rate_limited          → show the rate-limit copy
 *   404 no_passkeys           → OTP fallback (send an OTP, advance to the code step)
 *   401 passkey_verification_failed → failed (latch the "Send Login Code" button)
 *   portal 403                → authz-denied (show authzDeniedCopy)
 * Do not re-order or drop a case without re-clearing Gate-2.
 */

import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';

/** These pre-auth endpoints are hit with a raw fetch (not the app's apiFetch): a
 *  404 here is an expected "no passkeys yet" branch, not a telemetry-worthy error.
 *  `X-Requested-With` marks the request as same-origin XHR for the CSRF guard. */
export const XHR_HEADERS = {
  'Content-Type': 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
} as const;

/**
 * The full set of URL paths the door talks to. Portals differ
 * (`/api/vetting/...` vs `/api/auth/...`), so the component hardcodes none — the
 * adopter passes its own paths. `discover*` back the usernameless one-tap +
 * conditional-UI autofill; `passkey*` back the typed-email fast path; `otp*` are
 * the permanent recovery path.
 */
export interface LoginScreenEndpoints {
  /** POST — usernameless discoverable options (conditional UI + one-tap). */
  discoverOptions: string;
  /** POST — verify a discoverable assertion; resolves the account server-side. */
  discoverVerify: string;
  /** POST { email } — typed-email auth options; 404 ⇒ no passkeys, 503 ⇒ off. */
  passkeyOptions: string;
  /** POST { email, response } — verify the typed-email assertion. */
  passkeyVerify: string;
  /** POST { email } — email a 6-digit login code (enumeration-safe). */
  otpRequest: string;
  /** POST { email, code } — verify the login code. */
  otpVerify: string;
}

/** The verify payload the portal reads to persist its own session. Token portals
 *  read `token`/`role`; cookie portals ignore the body (the Set-Cookie did the
 *  work) — so every field is optional and unknown extras pass through. */
export interface LoginResult {
  ok?: boolean;
  token?: string;
  role?: string;
  email?: string;
  [key: string]: unknown;
}

/** Where a typed-email passkey attempt ended up — the frozen status contract. */
export type PasskeyOutcome =
  | 'signed-in'
  | 'no-passkeys'
  | 'unavailable'
  | 'cancelled'
  | 'authz-denied'
  | 'rate-limited'
  | 'failed';

/** Outcome of asking the server to email a login code. */
export type OtpRequestOutcome = 'sent' | 'rate-limited' | 'failed';

/** Outcome of submitting a login code. */
export type OtpVerifyOutcome =
  'signed-in' | 'invalid' | 'authz-denied' | 'rate-limited' | 'unavailable' | 'failed';

/** The WebAuthn ceremony, injected so tests need no real authenticator; in the
 *  component this wraps `startAuthentication({ optionsJSON })`. */
export type Authenticate = (
  options: PublicKeyCredentialRequestOptionsJSON,
) => Promise<AuthenticationResponseJSON>;

/** Every user-facing string, so a portal can override wording (defaults are the
 *  AUTH-8 exact copy). Split into the visual copy (AUTH-8) + the operational
 *  messages the reference LoginPages already ship. */
export interface LoginScreenCopy {
  // ── Step 1: Sign In (AUTH-8) ──
  signInHeading?: string;
  signInSubtitle?: string;
  passkeyButton?: string;
  passkeyButtonBusy?: string;
  emailLabel?: string;
  emailPlaceholder?: string;
  /** Primary label when the email path is the only path (otp-only / after fallback). */
  sendCodeButton?: string;
  sendingButton?: string;
  /** Ghost label when the email path is demoted below the passkey button. */
  continueWithEmail?: string;
  signingInButton?: string;
  emailFooter?: string;
  // ── Step 2: Enter Login Code (AUTH-8) ──
  codeHeading?: string;
  /** Rendered as: `${codeSubtitlePrefix}<strong>email</strong>`. */
  codeSubtitlePrefix?: string;
  codeLabel?: string;
  verifyButton?: string;
  verifyingButton?: string;
  backToEmail?: string;
  codeFooter?: string;
  // ── Operational messages ──
  rateLimited?: string;
  passkeyDismissed?: string;
  passkeyFailed?: string;
  otpSendFailed?: string;
  otpInvalid?: string;
  otpUnavailable?: string;
  verifyFailed?: string;
  genericError?: string;
  /** Fallback 403 message when the portal passes no `authzDeniedCopy`. */
  authzDenied?: string;
}

/** AUTH-8's exact strings + the reference LoginPages' operational wording. */
export const DEFAULT_LOGIN_COPY: Required<LoginScreenCopy> = {
  signInHeading: 'Sign In',
  signInSubtitle: 'Enter your email address to receive a login code.',
  passkeyButton: 'Sign in with a passkey',
  passkeyButtonBusy: 'Waiting for your passkey…',
  emailLabel: 'Email Address',
  emailPlaceholder: 'you@example.com',
  sendCodeButton: 'Send Login Code',
  sendingButton: 'Sending…',
  continueWithEmail: 'Continue with email',
  signingInButton: 'Signing in…',
  emailFooter: 'Check your email for a 6-digit code. The code expires in 10 minutes.',
  codeHeading: 'Enter Login Code',
  codeSubtitlePrefix: 'We sent a 6-digit code to ',
  codeLabel: 'Login Code',
  verifyButton: 'Verify Code',
  verifyingButton: 'Verifying…',
  backToEmail: 'Back to Email',
  codeFooter: 'Code expires in 10 minutes. Enter it above to sign in.',
  rateLimited: 'Too many requests. Please try again in an hour.',
  passkeyDismissed:
    'Passkey dismissed — no code has been emailed. Press "Send Login Code" for a code, or use the passkey button above to try again.',
  passkeyFailed:
    'Passkey sign-in didn’t work — press "Send Login Code" to email yourself a code instead.',
  otpSendFailed: 'Failed to send login code. Please try again.',
  otpInvalid: 'Invalid or expired code. Please try again.',
  otpUnavailable: 'Could not reach the sign-in service. Please try again in a moment.',
  verifyFailed: 'Verification failed. Please try again.',
  genericError: 'An error occurred. Please try again.',
  authzDenied:
    'You are not authorised to access this portal. Contact registrar@confessinganglicans.nz if you believe this is an error.',
};

/** The state setters the React shell hands the flow — kept as a tiny interface so
 *  the orchestration is drivable by spies in a test (no DOM required). */
export interface LoginSetters {
  setLoading: (value: boolean) => void;
  setError: (value: string | null) => void;
  setInfo: (value: string | null) => void;
  setOtpFallback: (value: boolean) => void;
  setStep: (value: 'email' | 'code') => void;
}

/** POST JSON to a pre-auth route with the shared XHR headers + credentials mode. */
export function postJson(
  url: string,
  body: unknown,
  credentials: RequestCredentials = 'same-origin',
  fetchImpl: typeof fetch = fetch,
): Promise<Response> {
  return fetchImpl(url, {
    method: 'POST',
    headers: XHR_HEADERS,
    credentials,
    body: JSON.stringify(body),
  });
}

/** True success = HTTP-ok verify AND a body the server did not mark `ok: false`. */
function bodyIsSignedIn(data: LoginResult | null): data is LoginResult {
  return Boolean(data) && (data as LoginResult).ok !== false;
}

// ── Usernameless (discoverable) passkey — the one-tap + conditional-UI path ──

/** Fetch discoverable auth options for the conditional-UI / one-tap ceremony. */
export async function fetchDiscoverOptions(
  endpoints: Pick<LoginScreenEndpoints, 'discoverOptions'>,
  credentials: RequestCredentials = 'same-origin',
  fetchImpl: typeof fetch = fetch,
): Promise<PublicKeyCredentialRequestOptionsJSON> {
  const res = await postJson(endpoints.discoverOptions, {}, credentials, fetchImpl);
  if (!res.ok) throw new Error('discover-options failed');
  return ((await res.json()) as { options: PublicKeyCredentialRequestOptionsJSON }).options;
}

/** Verify a discoverable assertion; the account resolves server-side from the
 *  credential (never a client-supplied email). */
export async function verifyDiscover(
  endpoints: Pick<LoginScreenEndpoints, 'discoverVerify'>,
  assertion: AuthenticationResponseJSON,
  credentials: RequestCredentials = 'same-origin',
  fetchImpl: typeof fetch = fetch,
): Promise<LoginResult> {
  const res = await postJson(
    endpoints.discoverVerify,
    { response: assertion },
    credentials,
    fetchImpl,
  );
  if (!res.ok) throw new Error(`discover-verify ${res.status}`);
  return (await res.json()) as LoginResult;
}

// ── Typed-email passkey — the submit fast path ──

export interface TypedEmailPasskeyArgs {
  email: string;
  endpoints: Pick<LoginScreenEndpoints, 'passkeyOptions' | 'passkeyVerify'>;
  authenticate: Authenticate;
  /** Called with the verify body on success (the portal persists its session). */
  onSignedIn: (result: LoginResult) => void;
  credentials?: RequestCredentials;
  fetchImpl?: typeof fetch;
}

/**
 * The typed-email passkey fast path. Fetches options, runs the ceremony,
 * verifies — mapping each status to the frozen outcome contract above. Never
 * throws: a network error resolves to 'failed'.
 */
export async function runTypedEmailPasskey({
  email,
  endpoints,
  authenticate,
  onSignedIn,
  credentials = 'same-origin',
  fetchImpl = fetch,
}: TypedEmailPasskeyArgs): Promise<PasskeyOutcome> {
  try {
    const optRes = await postJson(endpoints.passkeyOptions, { email }, credentials, fetchImpl);
    if (optRes.status === 404) return 'no-passkeys';
    if (optRes.status === 503) return 'unavailable';
    if (optRes.status === 429) return 'rate-limited';
    if (!optRes.ok) return 'failed';
    const { options } = (await optRes.json()) as { options: PublicKeyCredentialRequestOptionsJSON };

    let assertion: AuthenticationResponseJSON;
    try {
      assertion = await authenticate(options);
    } catch (err) {
      // NotAllowedError = dismissed; AbortError = superseded (e.g. an armed
      // conditional call). Both benign — never latch a scary failure.
      const name = (err as Error | null)?.name;
      return name === 'NotAllowedError' || name === 'AbortError' ? 'cancelled' : 'failed';
    }

    const verifyRes = await postJson(
      endpoints.passkeyVerify,
      { email, response: assertion },
      credentials,
      fetchImpl,
    );
    if (verifyRes.status === 403) return 'authz-denied';
    if (verifyRes.status === 429) return 'rate-limited';
    if (verifyRes.status === 401) return 'failed';
    if (!verifyRes.ok) return 'failed';

    const data = (await verifyRes.json()) as LoginResult | null;
    if (bodyIsSignedIn(data)) {
      onSignedIn(data);
      return 'signed-in';
    }
    return 'failed';
  } catch {
    return 'failed';
  }
}

/** The effect a passkey outcome has on the email step — the submit switch, pure. */
export interface PasskeyEffect {
  /** Proceed to email a login code (404/503 fall-through). */
  sendOtp: boolean;
  /** Latch the submit button to a plain "Send Login Code" (no surprise re-prompt). */
  otpFallback: boolean;
  error?: string;
  info?: string;
}

/** Map a passkey outcome to its email-step effect (message + fallback/latch). */
export function nextAfterPasskeyOutcome(
  outcome: Exclude<PasskeyOutcome, 'signed-in'>,
  copy: Required<LoginScreenCopy>,
  authzDeniedCopy: string,
): PasskeyEffect {
  switch (outcome) {
    case 'no-passkeys':
    case 'unavailable':
      return { sendOtp: true, otpFallback: false };
    case 'cancelled':
      return { sendOtp: false, otpFallback: true, info: copy.passkeyDismissed };
    case 'authz-denied':
      return { sendOtp: false, otpFallback: false, error: authzDeniedCopy };
    case 'rate-limited':
      return { sendOtp: false, otpFallback: false, error: copy.rateLimited };
    case 'failed':
      return { sendOtp: false, otpFallback: true, error: copy.passkeyFailed };
  }
}

// ── OTP — the permanent recovery path ──

export interface RequestOtpArgs {
  email: string;
  endpoints: Pick<LoginScreenEndpoints, 'otpRequest'>;
  credentials?: RequestCredentials;
  fetchImpl?: typeof fetch;
}

/** Ask the server to email a login code. Enumeration-safe: a 200 always advances
 *  to the code step whether or not the address is known (AUTH-6). */
export async function requestOtp({
  email,
  endpoints,
  credentials = 'same-origin',
  fetchImpl = fetch,
}: RequestOtpArgs): Promise<OtpRequestOutcome> {
  try {
    const res = await postJson(endpoints.otpRequest, { email }, credentials, fetchImpl);
    if (res.ok) return 'sent';
    if (res.status === 429) return 'rate-limited';
    return 'failed';
  } catch {
    return 'failed';
  }
}

export interface VerifyOtpArgs {
  email: string;
  code: string;
  endpoints: Pick<LoginScreenEndpoints, 'otpVerify'>;
  onSignedIn: (result: LoginResult) => void;
  credentials?: RequestCredentials;
  fetchImpl?: typeof fetch;
}

/** Submit a login code. 401 ⇒ invalid/expired · 403 ⇒ authz-denied · 429 ⇒
 *  verify lockout (AUTH-6) · 503 ⇒ directory unreachable. */
export async function verifyOtp({
  email,
  code,
  endpoints,
  onSignedIn,
  credentials = 'same-origin',
  fetchImpl = fetch,
}: VerifyOtpArgs): Promise<OtpVerifyOutcome> {
  try {
    const res = await postJson(endpoints.otpVerify, { email, code }, credentials, fetchImpl);
    if (res.status === 401) return 'invalid';
    if (res.status === 403) return 'authz-denied';
    if (res.status === 429) return 'rate-limited';
    if (res.status === 503) return 'unavailable';
    if (!res.ok) return 'failed';
    const data = (await res.json()) as LoginResult | null;
    if (bodyIsSignedIn(data)) {
      onSignedIn(data);
      return 'signed-in';
    }
    return 'invalid';
  } catch {
    return 'failed';
  }
}

/** Map an OTP verify outcome (other than signed-in) to its error message. */
export function messageForVerifyOutcome(
  outcome: Exclude<OtpVerifyOutcome, 'signed-in'>,
  copy: Required<LoginScreenCopy>,
  authzDeniedCopy: string,
): string {
  switch (outcome) {
    case 'authz-denied':
      return authzDeniedCopy;
    case 'rate-limited':
      return copy.rateLimited;
    case 'unavailable':
      return copy.otpUnavailable;
    case 'invalid':
      return copy.otpInvalid;
    case 'failed':
      return copy.verifyFailed;
  }
}

// ── Submit orchestration (the whole two-step flow, driven by setter callbacks) ──

/** Email a login code, then advance to the code step (or report why not). */
async function sendOtpCode(
  email: string,
  endpoints: Pick<LoginScreenEndpoints, 'otpRequest'>,
  copy: Required<LoginScreenCopy>,
  setters: LoginSetters,
  credentials: RequestCredentials,
  fetchImpl: typeof fetch,
): Promise<void> {
  const outcome = await requestOtp({ email, endpoints, credentials, fetchImpl });
  if (outcome === 'sent') {
    setters.setStep('code');
    setters.setLoading(false);
    return;
  }
  setters.setError(outcome === 'rate-limited' ? copy.rateLimited : copy.otpSendFailed);
  setters.setLoading(false);
}

export interface SubmitEmailArgs {
  email: string;
  /** Run the passkey fast path first? (`showPasskey && !otpFallback`.) */
  usePasskey: boolean;
  endpoints: LoginScreenEndpoints;
  copy: Required<LoginScreenCopy>;
  authzDeniedCopy: string;
  authenticate: Authenticate;
  onSignedIn: (result: LoginResult) => void;
  setters: LoginSetters;
  credentials?: RequestCredentials;
  fetchImpl?: typeof fetch;
}

/**
 * The email-step submit — including 1Password/browser autofill auto-submit.
 * Passkey FIRST when the account has one (no email fired); accounts without
 * passkeys (and otp-only mode) fall straight through to the login code.
 */
export async function submitEmail({
  email,
  usePasskey,
  endpoints,
  copy,
  authzDeniedCopy,
  authenticate,
  onSignedIn,
  setters,
  credentials = 'same-origin',
  fetchImpl = fetch,
}: SubmitEmailArgs): Promise<void> {
  const trimmed = email.trim();
  if (!trimmed) return;
  setters.setLoading(true);
  setters.setError(null);
  setters.setInfo(null);

  try {
    if (usePasskey) {
      const outcome = await runTypedEmailPasskey({
        email: trimmed,
        endpoints,
        authenticate,
        onSignedIn,
        credentials,
        fetchImpl,
      });
      if (outcome === 'signed-in') return; // onSignedIn already entered the app
      const effect = nextAfterPasskeyOutcome(outcome, copy, authzDeniedCopy);
      if (effect.error) setters.setError(effect.error);
      if (effect.info) setters.setInfo(effect.info);
      if (effect.otpFallback) setters.setOtpFallback(true);
      if (!effect.sendOtp) {
        setters.setLoading(false);
        return;
      }
    }
    await sendOtpCode(trimmed, endpoints, copy, setters, credentials, fetchImpl);
  } catch {
    setters.setError(copy.genericError);
    setters.setLoading(false);
  }
}

export interface SubmitCodeArgs {
  email: string;
  code: string;
  endpoints: Pick<LoginScreenEndpoints, 'otpVerify'>;
  copy: Required<LoginScreenCopy>;
  authzDeniedCopy: string;
  onSignedIn: (result: LoginResult) => void;
  setters: Pick<LoginSetters, 'setLoading' | 'setError'>;
  credentials?: RequestCredentials;
  fetchImpl?: typeof fetch;
}

/** The code-step submit — verify the 6-digit code, then hand off to the portal. */
export async function submitLoginCode({
  email,
  code,
  endpoints,
  copy,
  authzDeniedCopy,
  onSignedIn,
  setters,
  credentials = 'same-origin',
  fetchImpl = fetch,
}: SubmitCodeArgs): Promise<void> {
  if (code.length !== 6) return;
  setters.setLoading(true);
  setters.setError(null);

  const outcome = await verifyOtp({
    email: email.trim(),
    code,
    endpoints,
    onSignedIn,
    credentials,
    fetchImpl,
  });
  if (outcome === 'signed-in') return; // onSignedIn already entered the app
  setters.setError(messageForVerifyOutcome(outcome, copy, authzDeniedCopy));
  setters.setLoading(false);
}
